import type {
  GenerationImpactResult,
  GenerationImpactScenarioInput,
} from '../../types/generationImpact';
import {
  DEFAULT_BLOCK_SIZING_INCREMENT_MW,
  roundUpToIncrement,
  sizeUniformGenerationBlocks,
} from './sizeUniformGenerationBlocks';

/** Two blocks is the smallest fleet that survives an N-1 contingency at all. */
export const MIN_GRANULARITY_BLOCK_COUNT = 2;
export const DEFAULT_GRANULARITY_MAX_BLOCK_COUNT = 24;

/**
 * Readable subset of the swept curve. Shared by the workbench panel and the
 * frozen report study so the two surfaces cannot drift apart.
 */
export const GRANULARITY_LADDER_BLOCK_COUNTS: readonly number[] = [2, 3, 4, 6, 8, 12, 16, 24];

/**
 * Curated ladder rows, always including the configured block count so the
 * current scenario is locatable on the curve.
 */
export function selectGranularityLadderRows(
  sweep: GenerationBlockGranularitySweep,
): GenerationGranularityRow[] {
  if (sweep.status !== 'available') return [];
  const ladder = new Set([...GRANULARITY_LADDER_BLOCK_COUNTS, sweep.configuredBlockCount]);
  return sweep.rows.filter((row) => ladder.has(row.blockCount));
}

/** Which duty the swept fleet is sized against. */
export type GenerationGranularityBasis = 'critical-load' | 'reserve';

export interface GenerationGranularityRow {
  blockCount: number;
  /** Blocks still online after the single largest contingency. */
  onlineBlocksAfterContingency: number;
  /** Exact ISO gross MW per block needed to meet the basis after N-1. */
  requiredGrossMWPerBlock: number;
  /** `requiredGrossMWPerBlock` rounded up to the procurement increment. */
  suggestedGrossMWPerBlock: number;
  /**
   * Exact fleet nameplate: blockCount x requiredGrossMWPerBlock. Falls
   * monotonically with block count and carries the n / (n - 1) argument.
   */
  requiredInstalledGrossMW: number;
  /**
   * Procurement fleet nameplate: blockCount x suggestedGrossMWPerBlock. NOT
   * monotonic — see `incrementRoundingPenaltyMW`.
   */
  installedGrossMW: number;
  /**
   * `installedGrossMW - requiredInstalledGrossMW`. Every block pays the round-up
   * to the procurement increment, so the penalty scales with block count and can
   * overwhelm the granularity saving once block size approaches the increment.
   */
  incrementRoundingPenaltyMW: number;
  /** Site-condition net MW with the whole fleet running, procurement basis. */
  normalNetMW: number;
  /** Site-condition net MW after losing one block, procurement basis. */
  firmAfterContingencyMW: number;
  /** Normal net MW carried above the sizing basis, procurement basis. */
  reserveOverbuildMW: number;
  /** Exact installed nameplate relative to the finest granularity swept. */
  requiredInstalledRelativeToFinest: number;
  isConfiguredBlockCount: boolean;
}

export type GenerationBlockGranularitySweep =
  | {
      status: 'available';
      basis: GenerationGranularityBasis;
      /** MW the surviving fleet must deliver after N-1. */
      basisRequirementMW: number;
      configuredBlockCount: number;
      /** Site-condition net MW delivered per MW of ISO gross installed. */
      netDeliveryFactor: number;
      incrementMW: number;
      rows: GenerationGranularityRow[];
    }
  | { status: 'unavailable'; reason: string };

export interface GenerationGranularitySweepOptions {
  basis?: GenerationGranularityBasis;
  maxBlockCount?: number;
  incrementMW?: number;
}

/**
 * Sweep N-1 fleet sizing across contingency granularities.
 *
 * `sizeUniformGenerationBlocks` answers "at my block count, how large must each
 * block be?". This answers the prior question: how does installed capacity move
 * as the contingency unit gets smaller?
 *
 * N-1 overbuild is set by the size of the largest contingency, not the size of
 * the load, so a fleet of n identical blocks must install roughly `n / (n - 1)`
 * times the delivered requirement. That ratio is 2.0 at two blocks (a full
 * spare) and 1.05 at twenty-two, which is the whole granularity argument.
 *
 * No thermodynamics are repeated here. Net output is linear in ISO gross MW for
 * a uniform fleet (see `sizeUniformGenerationBlocks`), including the CCS
 * parasitic, which is allocated per unit in proportion to unit gross output and
 * is therefore invariant to block count. The exact solver supplies the delivery
 * coefficient; this function only rescales it.
 *
 * Units: MW throughout. `requiredGrossMWPerBlock`, `suggestedGrossMWPerBlock`
 * and `installedGrossMW` are ISO gross; `normalNetMW`, `firmAfterContingencyMW`
 * and `reserveOverbuildMW` are site-condition net.
 */
export function sweepGenerationBlockGranularity(
  input: GenerationImpactScenarioInput,
  result: GenerationImpactResult,
  options: GenerationGranularitySweepOptions = {},
): GenerationBlockGranularitySweep {
  const basis = options.basis ?? 'reserve';
  const incrementMW = options.incrementMW ?? DEFAULT_BLOCK_SIZING_INCREMENT_MW;
  const maxBlockCount = options.maxBlockCount ?? DEFAULT_GRANULARITY_MAX_BLOCK_COUNT;

  if (!Number.isInteger(maxBlockCount) || maxBlockCount < MIN_GRANULARITY_BLOCK_COUNT) {
    return {
      status: 'unavailable',
      reason: `The granularity sweep needs a whole maximum block count of at least ${MIN_GRANULARITY_BLOCK_COUNT}.`,
    };
  }

  // Reuse the uniform-fleet guards: identical one-unit blocks, independent
  // contingency groups, no input gaps, positive critical load.
  const sizing = sizeUniformGenerationBlocks(input, result, incrementMW);
  if (sizing.status === 'unavailable') return sizing;

  const basisRequirementMW = basis === 'reserve'
    ? result.capacity.reserveRequiredMW
    : result.capacity.criticalLoadMW;
  if (!Number.isFinite(basisRequirementMW) || basisRequirementMW <= 0) {
    return { status: 'unavailable', reason: 'A positive sizing requirement is needed for a granularity sweep.' };
  }

  const { netDeliveryFactor } = sizing;
  const rows: GenerationGranularityRow[] = [];
  for (let blockCount = MIN_GRANULARITY_BLOCK_COUNT; blockCount <= maxBlockCount; blockCount += 1) {
    const onlineBlocksAfterContingency = blockCount - 1;
    const survivingFleetFactor = onlineBlocksAfterContingency * netDeliveryFactor;
    const requiredGrossMWPerBlock = basisRequirementMW / survivingFleetFactor;
    const suggestedGrossMWPerBlock = roundUpToIncrement(requiredGrossMWPerBlock, incrementMW);
    const requiredInstalledGrossMW = blockCount * requiredGrossMWPerBlock;
    const installedGrossMW = blockCount * suggestedGrossMWPerBlock;
    const normalNetMW = installedGrossMW * netDeliveryFactor;
    rows.push({
      blockCount,
      onlineBlocksAfterContingency,
      requiredGrossMWPerBlock,
      suggestedGrossMWPerBlock,
      requiredInstalledGrossMW,
      installedGrossMW,
      incrementRoundingPenaltyMW: installedGrossMW - requiredInstalledGrossMW,
      normalNetMW,
      firmAfterContingencyMW: onlineBlocksAfterContingency * suggestedGrossMWPerBlock * netDeliveryFactor,
      reserveOverbuildMW: normalNetMW - basisRequirementMW,
      requiredInstalledRelativeToFinest: 1,
      isConfiguredBlockCount: blockCount === sizing.blockCount,
    });
  }

  const finestRequiredGrossMW = rows[rows.length - 1]?.requiredInstalledGrossMW ?? 0;
  const normalised = finestRequiredGrossMW > 0
    ? rows.map((row) => ({
        ...row,
        requiredInstalledRelativeToFinest: row.requiredInstalledGrossMW / finestRequiredGrossMW,
      }))
    : rows;

  return {
    status: 'available',
    basis,
    basisRequirementMW,
    configuredBlockCount: sizing.blockCount,
    netDeliveryFactor,
    incrementMW,
    rows: normalised,
  };
}
